import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../../utils/ui-components.js";
import { Chart } from "chart.js";
import { html } from "lit";
import { createRef, ref } from "lit/directives/ref.js";
import { CompletionEvent, Api } from "../../api.js";

@customElement("completion-events")
export class CompletionEvents extends BaseElement {
    @property({ type: String })
    userId?: string;

    @state()
    private dateFrom: Date;

    @state()
    private dateTo: Date;

    @state()
    private events: CompletionEvent[] = [];

    @state()
    private loading = true;

    @state()
    private loadingMore = false;

    @state()
    private error = "";

    @state()
    private hasMore = true;

    @state()
    private currentPage = 0;

    private readonly pageSize = 20;

    @state()
    private stats: { date: string; input_tokens: number; output_tokens: number }[] = [];

    private loadMoreRef = createRef<HTMLDivElement>();
    private observer?: IntersectionObserver;

    constructor() {
        super();
        // Initialize date range to last 30 days
        this.dateTo = new Date();
        this.dateFrom = new Date();
        this.dateFrom.setDate(this.dateFrom.getDate() - 30);
    }

    async firstUpdated() {
        // Initialize date inputs with the current range
        const fromInput = this.renderRoot.querySelector<HTMLInputElement>("#dateFrom");
        const toInput = this.renderRoot.querySelector<HTMLInputElement>("#dateTo");

        if (fromInput && toInput) {
            fromInput.value = this.dateFrom.toISOString().split("T")[0];
            toInput.value = this.dateTo.toISOString().split("T")[0];
        }

        await Promise.all([this.loadEvents(), this.loadStats()]);

        // Set up infinite scroll
        this.observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && this.hasMore && !this.loading && !this.loadingMore) {
                this.loadEvents(true);
            }
        });

        if (this.loadMoreRef.value) {
            this.observer.observe(this.loadMoreRef.value);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.observer?.disconnect();
    }

    private async loadStats() {
        try {
            this.stats = await Api.getCompletionStats({
                userId: this.userId,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
            });
            this.renderChart();
        } catch (e) {
            console.error("Failed to load stats:", e);
        }
    }

    private handleDateChange() {
        const fromInput = this.renderRoot.querySelector<HTMLInputElement>("#dateFrom");
        const toInput = this.renderRoot.querySelector<HTMLInputElement>("#dateTo");

        if (fromInput?.value && toInput?.value) {
            this.dateFrom = new Date(fromInput.value);
            this.dateTo = new Date(toInput.value);
            this.loadStats();
        }
    }

    private renderChart() {
        const ctx = this.renderRoot.querySelector<HTMLCanvasElement>("#statsChart");
        if (!ctx) return;

        new Chart(ctx, {
            type: "bar",
            data: {
                labels: this.stats.map((s) => s.date),
                datasets: [
                    {
                        label: "Input Tokens",
                        data: this.stats.map((s) => s.input_tokens),
                        backgroundColor: "rgb(59, 130, 246)",
                        borderColor: "rgb(59, 130, 246)",
                    },
                    {
                        label: "Output Tokens",
                        data: this.stats.map((s) => s.output_tokens),
                        backgroundColor: "rgb(147, 51, 234)",
                        borderColor: "rgb(147, 51, 234)",
                    },
                ],
            },
            options: {
                responsive: true,
                aspectRatio: 2.5,
                scales: {
                    x: {
                        stacked: false,
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: "Tokens",
                        },
                    },
                },
                plugins: {
                    legend: {
                        position: "top",
                    },
                },
            },
        });
    }

    private async loadEvents(loadMore = false) {
        if (loadMore) {
            this.loadingMore = true;
        } else {
            this.loading = true;
            this.currentPage = 0;
            this.events = [];
        }

        try {
            const result = await Api.getCompletionEvents({
                page: this.currentPage,
                pageSize: this.pageSize,
                userId: this.userId,
            });

            if (loadMore) {
                this.events = [...this.events, ...result.events];
            } else {
                this.events = result.events;
            }

            this.hasMore = (this.currentPage + 1) * this.pageSize < result.total;
            if (this.hasMore) this.currentPage++;
        } catch (e) {
            this.error = "Failed to load completion events";
        } finally {
            this.loading = false;
            this.loadingMore = false;
        }
    }

    render() {
        return html`
            <div class="bg-white rounded-lg shadow">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Input Tokens</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Output Tokens</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Tokens</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            ${this.events.map(
                                (event) => html`
                                    <tr class="hover:bg-gray-50">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">${new Date(event.timestamp).toLocaleString()}</td>
                                        <td class="px-6 py-4 text-sm">${event.session_id || "-"}</td>
                                        <td class="px-6 py-4 text-sm">${event.input_tokens}</td>
                                        <td class="px-6 py-4 text-sm">${event.output_tokens}</td>
                                        <td class="px-6 py-4 text-sm">${event.total_tokens}</td>
                                    </tr>
                                `
                            )}
                        </tbody>
                    </table>
                </div>
                ${this.hasMore
                    ? html`
                          <div ${ref(this.loadMoreRef)} class="p-4 flex justify-center">
                              ${this.loadingMore ? html`<div class="text-gray-500">Loading...</div>` : html`<div class="h-8"></div>`}
                          </div>
                      `
                    : null}
            </div>
        `;
    }
}
